/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * @module bulb-resize/bulbresizecommand
 */

import { Command, type ModelElement } from 'ckeditor5';

/**
 * A custom bulb implementation of the Image Resize command from CKEditor. Hopefully in the
 * future, this can be used for all blocks and not just images.
 *
 * @extends Command
 */
export default class BulbResizeCommand extends Command {

  /**
   * @inheritDoc
   */
  public override refresh(): void {
    const element = this.editor.model.document.selection.getSelectedElement();
    this.isEnabled = this.doesBlockAllowResize(element);

    if (!element || !element.hasAttribute('width')) {
      this.value = null;
    } else {
      this.value = {
        width: element.getAttribute('width'),
        height: null,
      };
    }
  }

  /**
   * Executes the command.
   *
   *    // Sets the width to 50%:
   *    editor.execute( 'bulbResize', { width: '50%' } );
   *
   *    // Removes the width attribute:
   *    editor.execute( 'bulbResize', { width: null } );
   *
   * @param {Object} options
   * @param {String|null} options.width The new width of the image.
   */
  public override execute(options: { width: string | null; }): void {
    const { model } = this.editor;
    const blockElement = model.document.selection.getSelectedElement();

    model.change((writer) => {
      writer.setAttribute('width', options.width, blockElement);
    });
  }

  /**
   * Checks if the provided model element is an acceptable block that can be resized.
   *
   * @param {module:engine/model/element~ModelElement} modelElement
   * @returns {Boolean}
   */
  private doesBlockAllowResize(modelElement: ModelElement): boolean {
    return !!modelElement && (modelElement.is('element', 'image') || modelElement.is('element', 'video')
      || modelElement.is('element', 'audio') || modelElement.is('element', 'embed')
      || modelElement.is('element', 'attachedFile'));
  }
}
